import { useEffect, useRef, useState, useContext } from 'react';
import { useSession } from 'next-auth/react';
import toast from 'react-hot-toast';
import { ArrowBackIcon, ArrowForwardIcon } from '@chakra-ui/icons';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import {
  RiSettings4Fill,
  RiArrowDownSLine,
  RiArrowUpSLine,
} from 'react-icons/ri';
import { BiCog } from 'react-icons/bi';
import { getToken } from '../firebaseClient';
import { textStyle } from 'theme/names';
import { MainContext } from 'context/mainContext';
import { getCreditBack, useCredit, getPlan } from 'controllers/subscription';
import {
  angleTags,
  formatTags,
  structureTags,
  toneTags,
  variationTags,
} from 'components/post-generator/tags-selector/static-values';
import TagsSelector from 'components/post-generator/tags-selector';
import { TextArea } from 'components/post-generator/TextArea';
import { TalQAModal } from 'components/post-generator/talQAModal';
import ProtectedRoute from 'components/protected-route';
import Tweets from 'components/tweets';
import { Page } from 'components/page';
import { getAccount, updateUser } from 'utils/sessionHelper';
import { useFirebaseUser } from 'utils/useFirebaseUser';
import {
  Box,
  Button,
  Card,
  Fade,
  Flex,
  Text,
  useDisclosure,
  Checkbox,
  SkeletonCircle,
  SkeletonText,
  Divider,
  Tooltip,
  Center,
} from '@chakra-ui/react';
import { AiButton } from 'components/common/AiButton/AiButton';
import PuffLoader from 'react-spinners/PuffLoader';
import {
  removeMarkdown,
  addNewLineThree,
  removeHashtagsAndEmojis,
} from 'utils/tweetUtils';
import * as analytics from '../utils/analytics';
import { UpgradePopup } from 'components/upgrade-popup';
import { ez4castApiUrls } from 'utils/ez4cast';
import { PostGenerationParameters } from 'types/ai';
import { useGodUser } from 'hooks/isGod';
import { captureException } from '@sentry/nextjs';
export default (_props: any) => {
  const { data: session, status } = useSession() ?? {};
  const loading = status === 'loading';
  // Selection States
  const [selectedFormatsTag, setSelectedFormatTag] = useState('');
  const [selectedAngleTag, setSelectedAngleTag] = useState('');
  const [selectedToneTags, setSelectedToneTags] = useState<string[]>([]); // max 3
  const [selectedStructureTag, setSelectedStructureTag] = useState('');
  const [selectedVariationTags, setSelectedVariationsTag] = useState<string[]>(
    []
  );

  // Modal state
  const [isTagsSelectionOpen, setIsTagsSelectionOpen] = useState(false);

  // Loading states
  const [suggestTopicLoading, setSuggestTopicLoading] = useState(false);
  const [generatePostLoading, setGeneratePostLoading] = useState(false);

  // Text values
  const [textAreaValue, setTextAreaValue] = useState('');
  const [topicsSuggestions, setTopicsSuggestions] = useState<string[]>([]);

  const [currentTopicIndex, setCurrentTopicIndex] = useState(0);

  const [linkedinPosts, setLinkedinPosts] = useState<any>([]);
  const [saveTagsForFuturePost, setSaveTagsForFuturePost] = useState(false);

  const mainContext = useContext(MainContext);
  const isAuthenticated = useFirebaseUser();

  //talGPT
  const [talGPTQuestions, setTalGPTQuestions] = useState({
    targetAudience: '',
    jobTitle: '',
    writingStyle: '',
    industry: '',
    company: '',
    about: '',
  });

  //talGPT Modal state
  const [isTalQAOpen, setIsTalQAOpen] = useState(false);
  const finalModalRef = useRef<any>(null);

  //page loading state while checking talGPTQAs
  const [isLoadingQAs, setIsLoadingQAs] = useState(true);

  const {
    isOpen: isQAModalOpen,
    onOpen: onOpenQAModal,
    onClose: onCloseQAModal,
  } = useDisclosure();

  const account = getAccount(session);
  const plan = getPlan(session?.user);
  const isStarterPlan = plan === 'start';

  const numberOfTagsSelected =
    (selectedFormatsTag.length > 0 ? 1 : 0) +
    (selectedAngleTag.length > 0 ? 1 : 0) +
    selectedToneTags.length +
    (selectedStructureTag.length > 0 ? 1 : 0);

  const { isGod } = useGodUser();
  const postGeneratorApiUrls = isGod
    ? ez4castApiUrls.postGeneratorGeneratePost.prod
    : ez4castApiUrls.postGeneratorGeneratePostOld.prod;
  const suggestionsApiUrls = ez4castApiUrls.postGeneratorSuggestTopics.prod;

  const getPosts = async (
    params: PostGenerationParameters,
    session: any,
    account: any
  ) => {
    const token = await getToken(session, 'aiPostGenerator');
    const res = await fetch(postGeneratorApiUrls, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        tokenuserid: session.user.uid,
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        ...params,
        token,
        isInfluentLeader: true,
        language: getAccount(session)?.languagePreferred || 'English',
      }),
    });

    const result = await res.json();

    if (!result.success) {
      toast.error('An error occured, please try again later');
      captureException(new Error('Formatting error'));
      throw new Error('Formatting error');
    }

    const parsedResult = JSON.parse(result.chat);

    const formatResponse = parsedResult.posts.map((postObj: any) => {
      const post = postObj?.post;
      const variationType = postObj?.variationType;
      const finalPost = addNewLineThree(
        addNewLineThree(removeMarkdown(post.replace(/"/g, '')))
      );
      return {
        fullText: finalPost,
        full_text: finalPost,
        variationType,
        user: {
          name: account.name,
          description: account.description,
          image: account?.accounts?.[0]?.image || account.image,
          profile_image_url_https:
            account?.accounts?.[0]?.image || account.image,
          username: account.username,
          twUserName: account.username,
        },
      };
    });

    let formattedResponse = {
      data: {
        tweets: formatResponse,
      },
    };

    return formattedResponse;
  };

  const getSuggestions = async (
    description: string,
    session: any,
    useBestAi: boolean
  ) => {
    const token = await getToken(session, 'aiPostGenerator');
    const res = await fetch(suggestionsApiUrls, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        tokenuserid: session.user.uid,
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        description,
        useBestAi,
        token,
        isInfluentLeader: true,
        language: getAccount(session)?.languagePreferred || 'English',
      }),
    });

    const result = await res.json();

    if (!result.success) {
      toast.error('An error occured, please try again later');
      captureException(new Error('Error in getSuggestions'));
      return;
    }

    return result;
  };

  const suggestTopic = async () => {
    try {
      if (!session || isTalQAOpen) {
        return;
      }

      setSuggestTopicLoading(true);

      let useBestAi = await useCredit(
        session,
        mainContext,
        'creditsBestai',
        false
      );
      const description = `${account?.description}.\n\n ${talGPTQuestions.about}.\n\n My Target Audiences are ${talGPTQuestions.targetAudience}.\n\n I am a ${talGPTQuestions.jobTitle}.\n\n My writing style is ${talGPTQuestions.writingStyle}.\n\n The industry I work in is ${talGPTQuestions.industry}.\n\n My company ${talGPTQuestions.company}.\n\n`;

      const suggestions = await getSuggestions(description, session, useBestAi);
      if (suggestions?.chat?.length && suggestions?.chat?.length > 0) {
        suggestions.chat = suggestions.chat.map((suggestion) => {
          const removeStuff = removeHashtagsAndEmojis(suggestion);
          return removeStuff;
        });
        setTopicsSuggestions(suggestions.chat);
        setTextAreaValue(suggestions.chat[0]);
      }

      setSuggestTopicLoading(false);
    } catch (error) {
      console.log({ error });
      captureException(error);
      setSuggestTopicLoading(false);
      return;
    }
  };

  const getSetTalQAVariations = async () => {
    //get QAs from database and set them in state
    //get varationTags from database and set them in state
    setIsLoadingQAs(true);
    const postGenQA = await getAccount(session)?.postGenQA;

    if (Boolean(postGenQA) === false) {
      setIsTalQAOpen(true);
      await updateUser(session, {
        postGenQA: {},
      });
    } else if (Object.keys(postGenQA).length === 0) {
      setIsTalQAOpen(true);
    } else {
      //set states
      setIsTalQAOpen(false);
      setTalGPTQuestions(postGenQA);
    }
    setIsLoadingQAs(false);
  };

  const generatePost = async () => {
    if (!session) {
      return;
    }
    setGeneratePostLoading(true);
    try {
      let useBestAi = await useCredit(
        session,
        mainContext,
        'creditsBestai',
        false,
        selectedVariationTags.length
      );
      setIsTagsSelectionOpen(false);

      const params = {
        topic: textAreaValue,
        format: selectedFormatsTag,
        angle: selectedAngleTag,
        tone: selectedToneTags,
        structure: selectedStructureTag,
        useBestAi,
        variations: selectedVariationTags,
        targetAudience: talGPTQuestions.targetAudience,
        jobTitle: talGPTQuestions.jobTitle,
        writingStyle: talGPTQuestions.writingStyle,
        industry: talGPTQuestions.industry,
        company: talGPTQuestions.company,
        about: talGPTQuestions.about,
      };

      await updateUser(session, {
        formatsTag: selectedFormatsTag,
        angleTag: selectedAngleTag,
        toneTags: selectedToneTags,
        structureTag: selectedStructureTag,
        variationTags: selectedVariationTags,
        saveTagsForFuturePost,
      });

      const posts = await getPosts(params, session, getAccount(session));
      const newTweets = posts?.data?.tweets ? posts?.data?.tweets : [];
      analytics.log('posts_generated');
      setLinkedinPosts(newTweets);
      setGeneratePostLoading(false);
    } catch (error) {
      setGeneratePostLoading(false);
      getCreditBack(session, 'creditsBestai', selectedVariationTags.length);
      console.log({ error });
      captureException(error);
    }
  };

  const handleTopicChange = (increment: number) => {
    setCurrentTopicIndex((prevIndex) => prevIndex + increment);
  };

  const handleSelectedToneTags = (value: string) => {
    if (selectedToneTags.length === 3 && !selectedToneTags.includes(value)) {
      toast.error('You can only select 3 tone tags');
      return;
    }

    if (selectedToneTags.includes(value)) {
      setSelectedToneTags(selectedToneTags.filter((tag) => tag !== value));
    } else {
      setSelectedToneTags([...selectedToneTags, value]);
    }
  };

  const handleVariationTags = (value: string) => {
    if (
      selectedVariationTags.length > 3 &&
      !selectedVariationTags.includes(value)
    ) {
      toast.error('You can only select 3 variation tags');
      return;
    }

    if (selectedVariationTags.includes(value)) {
      setSelectedVariationsTag(
        selectedVariationTags.filter((tag) => tag !== value)
      );
    } else {
      setSelectedVariationsTag([...selectedVariationTags, value]);
    }
  };

  const handleSelectedTags = (
    value: string,
    getter: string,
    setter: (value: string) => void
  ) => {
    if (value === getter) {
      setter('');
      return;
    }
    setter(value);
  };

  const handleOnCloseQAModal: () => Promise<void> = async () => {
    await getSetTalQAVariations();
    onCloseQAModal();
  };

  useEffect(() => {
    if (getAccount(session)?.id) {
      getSetTalQAVariations();
      if (getAccount(session)?.saveTagsForFuturePost) {
        //uncomment below when post formatting tags are re-introduced
        getAccount(session)?.formatsTag &&
          setSelectedFormatTag(getAccount(session)?.formatsTag);
        getAccount(session)?.angleTag &&
          setSelectedAngleTag(getAccount(session)?.angleTag);
        getAccount(session)?.toneTags &&
          setSelectedToneTags(getAccount(session)?.toneTags);
        getAccount(session)?.structureTag &&
          setSelectedStructureTag(getAccount(session)?.structureTag);
        getAccount(session)?.variationTags &&
          setSelectedVariationsTag(getAccount(session)?.variationTags);
        getAccount(session)?.saveTagsForFuturePost &&
          setSaveTagsForFuturePost(getAccount(session)?.saveTagsForFuturePost);
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && getAccount(session)?.id) {
      updateUser(session, {
        saveTagsForFuturePost,
      });

      if (!saveTagsForFuturePost) {
        updateUser(session, {
          formatsTag: '',
          angleTag: '',
          toneTags: [],
          structureTag: '',
          variationTags: [],
        });
      }
    }
  }, [saveTagsForFuturePost]);

  useEffect(() => {
    if (topicsSuggestions.length !== 0) {
      setTextAreaValue(topicsSuggestions[currentTopicIndex]);
    }
  }, [currentTopicIndex]);

  useEffect(() => {
    if (selectedVariationTags.length === 0) {
      selectedVariationTags.push('actionable');
    }
  }, []);

  if (!(loading || session)) {
    return <ProtectedRoute />;
  }

  return (
    <Page
      forceCloseScheduler={true}
      title='Post generator'
      titleIconWrapperProps={{ gap: 3 }}
      description='Craft posts that answer your specific needs.'
      descriptionWrapperProps={{ pb: '5' }}
    >
      {isStarterPlan ? (
        <UpgradePopup
          currentPlan={plan}
          feature='Post Generator'
          availablePlans={['grow', 'enterprise']}
        />
      ) : !isLoadingQAs ? (
        <Box pb='10' maxW='700' position={'relative'}>
          <Box
            position={'absolute'}
            top={['-108px', '-84px']}
            left={['242px', '-60px']}
            w={'32px'}
          >
            <Tooltip
              placement='bottom-start'
              label='Update Post Generator Settings'
            >
              <Box>
                <Button
                  onClick={(e) => setIsTalQAOpen(true)}
                  variant={'primary'}
                  isDisabled={generatePostLoading}
                >
                  <RiSettings4Fill />
                </Button>
              </Box>
            </Tooltip>
          </Box>
          <Flex justifyContent='space-between' alignItems='center' pb='5'>
            <Text
              fontSize={['xl', '2xl']}
              fontWeight='600'
              textStyle={textStyle['body.medium.standard']}
            >
              What is your post about ?
            </Text>

            <Tooltip
              placement='top-start'
              label='Brainstorm an interesting topic for your post based on your AI settings. Will consume 1 credit.'
            >
              <Box>
                <AiButton
                  onClick={suggestTopic}
                  isLoading={suggestTopicLoading}
                  isDisabled={isLoadingQAs || isQAModalOpen}
                >
                  Suggest Topic
                </AiButton>
              </Box>
            </Tooltip>
          </Flex>

          <Card paddingTop='2' paddingLeft='4' ml={6}>
            <TextArea
              props={{
                placeholder: 'Provide a general idea for your post',
                value: textAreaValue,
                onChange: (e) => setTextAreaValue(e.target.value),
                ref: finalModalRef,
              }}
            />
            {topicsSuggestions.length > 0 && (
              <Box display='flex' justifyContent='flex-end'>
                <Button
                  variant='transparent'
                  onClick={() => handleTopicChange(-1)}
                  isDisabled={currentTopicIndex === 0}
                  color='#0A66C2'
                  leftIcon={<ArrowBackIcon />}
                >
                  prev.
                </Button>
                <Button
                  variant='transparent'
                  onClick={() => handleTopicChange(1)}
                  isDisabled={
                    currentTopicIndex === topicsSuggestions.length - 1
                  }
                  color='#0A66C2'
                  rightIcon={<ArrowForwardIcon />}
                >
                  next
                </Button>
              </Box>
            )}
          </Card>

          <Flex pt='10' gap='4' alignItems='flex-start' flexDir={'column'}>
            <Text
              fontSize={['xl', '2xl']}
              fontWeight='600'
              textStyle={textStyle['body.medium.standard']}
            >
              What kind of variations do you want to generate generate?
            </Text>
            <Box>
              <TagsSelector
                title='Post Types: '
                tags={variationTags}
                selectedTag={(tag: string) =>
                  selectedVariationTags.includes(tag)
                }
                setSelectedTag={handleVariationTags}
                containerStyle={{
                  paddingTop: 0,
                  marginTop: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 9,
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  marginBottom: 15,
                }}
                tagContainerStyle={{
                  marginTop: 3,
                  marginBottom: 15,
                  paddingTop: 0,
                }}
                titleStyle={{
                  paddingLeft: 0,
                  marginBottom: 10,
                }}
              />
            </Box>
          </Flex>
          <Text
            fontSize='md'
            as='i'
            textStyle={textStyle['body.medium.standard']}
          >
            You will consume 1 GPT-4 credit per variant.
          </Text>

          <Box pt='10'>
            <Flex gap='5' alignItems='center' justifyContent='space-between'>
              <AiButton
                isDisabled={
                  textAreaValue.length === 0 || isLoadingQAs || isQAModalOpen
                }
                onClick={generatePost}
                isLoading={generatePostLoading}
                size='lg'
                fontSize={'md'}
                containerProps={{ h: '45px' }}
              >
                Generate Posts
              </AiButton>

              <Flex
                alignItems='center'
                gap='2'
                onClick={() => setIsTagsSelectionOpen(!isTagsSelectionOpen)}
                cursor='pointer'
              >
                <BiCog />
                <Text
                  fontSize='md'
                  fontWeight='600'
                  textStyle={textStyle['body.medium.standard']}
                >
                  Format your post
                </Text>
                <Box
                  display='flex'
                  backgroundColor='#263754'
                  color='white'
                  width='20px'
                  height='20px'
                  borderRadius='5px'
                  fontSize='xs'
                  justifyContent='center'
                  alignItems='center'
                >
                  {numberOfTagsSelected}
                </Box>
                {isTagsSelectionOpen ? (
                  <RiArrowUpSLine />
                ) : (
                  <RiArrowDownSLine />
                )}
              </Flex>
            </Flex>
          </Box>
          <Box pb='20'>
            <Fade in={isTagsSelectionOpen}>
              {isTagsSelectionOpen && (
                <Box>
                  <Flex gap='2' py='5' alignItems='start'>
                    <AiOutlineInfoCircle size='30' color='#0A66C2' />
                    <Text
                      fontSize='md'
                      textStyle={textStyle['body.medium.primary']}
                    >
                      Selecting tags allows you to have more control over the
                      kind of posts that are being generated through the post
                      generator: format, length, angle, tone, etc.
                    </Text>
                  </Flex>

                  <Flex
                    py='2'
                    justifyContent='space-between'
                    alignItems='center'
                  >
                    <Flex flex='1'>
                      <Text
                        fontSize='large'
                        fontWeight='semibold'
                        textStyle={textStyle['body.medium.standard']}
                      >
                        Select up to 5 different tags for your posts
                      </Text>
                    </Flex>

                    <Flex gap='2' alignItems='center'>
                      <Checkbox
                        width='auto'
                        defaultChecked={saveTagsForFuturePost}
                        checked={saveTagsForFuturePost}
                        onChange={() =>
                          setSaveTagsForFuturePost((oldValue) => !oldValue)
                        }
                      />

                      <Text
                        fontSize='md'
                        textStyle={textStyle['body.medium.standard']}
                      >
                        Save tags for future posts
                      </Text>
                    </Flex>
                  </Flex>

                  <TagsSelector
                    title='Format tags'
                    tags={formatTags}
                    selectedTag={(tag: string) => selectedFormatsTag === tag}
                    setSelectedTag={(e) =>
                      handleSelectedTags(
                        e,
                        selectedFormatsTag,
                        setSelectedFormatTag
                      )
                    }
                  />

                  <TagsSelector
                    title='Tone tags'
                    tags={toneTags}
                    selectedTag={(tag: string) =>
                      selectedToneTags.includes(tag)
                    }
                    setSelectedTag={handleSelectedToneTags}
                  />

                  <TagsSelector
                    title='Angle tags'
                    tags={angleTags}
                    selectedTag={(tag: string) => selectedAngleTag === tag}
                    setSelectedTag={(e) =>
                      handleSelectedTags(
                        e,
                        selectedAngleTag,
                        setSelectedAngleTag
                      )
                    }
                  />

                  <TagsSelector
                    title='Structure tags'
                    titleInfo='(adapted for promotional posts)'
                    tags={structureTags}
                    selectedTag={(tag: string) => selectedStructureTag === tag}
                    setSelectedTag={(e) =>
                      handleSelectedTags(
                        e,
                        selectedStructureTag,
                        setSelectedStructureTag
                      )
                    }
                  />
                </Box>
              )}
            </Fade>
          </Box>

          {generatePostLoading ? (
            <Flex gap='5'>
              {Array.from(
                { length: selectedVariationTags.length },
                (_, index) => index + 1
              ).map((item, index) => (
                <Box
                  key={'postLoading' + index}
                  h='326'
                  width={'300px'}
                  boxShadow='lg'
                  bg='white'
                  borderRadius={'10px'}
                >
                  <Box padding={4}>
                    <SkeletonCircle size='5' />
                  </Box>
                  <Divider />
                  <Box padding={4}>
                    <SkeletonText
                      mt='4'
                      noOfLines={9}
                      spacing='4'
                      skeletonHeight='2'
                    />
                  </Box>
                </Box>
              ))}
            </Flex>
          ) : (
            linkedinPosts && (
              <Tweets
                tweets={linkedinPosts}
                disableBlur={true}
                disableBookmarkButton={true}
                disableSaveCsv={true}
                disableEmbed={true}
                disableTryAgain={true}
                disableHideButton={true}
                disableRequest={true}
                disableSubMenu={true}
                disableName={false}
                isVariation={true}
                disableAddToQueue={false}
                disableTweetSelection={true}
                displayVariationLabel={true}
                type='linkedin'
              />
            )
          )}
          <TalQAModal
            isQAModalOpen={isTalQAOpen && !isLoadingQAs}
            onCloseQAModal={handleOnCloseQAModal}
            setTalGPTQAs={setTalGPTQuestions}
            currentTalGPTQAs={talGPTQuestions}
            session={session}
            finalRef={finalModalRef}
          />
        </Box>
      ) : (
        <Center h='100px' w='100%'>
          <PuffLoader
            color={'#0A66C2'}
            loading={isLoadingQAs}
            cssOverride={{
              display: 'block',
              margin: '0 auto',
              borderColor: 'red',
            }}
            size={50}
          />
        </Center>
      )}
    </Page>
  );
};
